import axios from 'axios';
import ReconnectingWebSocket from 'reconnecting-websocket';

import { IOrderbook, ITickerSocket, TradeSocket } from 'types/cex/spot';
import { IChartTimeframe, KLine } from 'types/charts';
import { normalizeNumber } from 'utils/formats/normalizeNumber';

import { BaseStrategy, IInitArgs } from '../base_strategy';
import { aggregateOrderbook } from '../utils';

const COINBASE_REST_BASE = 'https://api.exchange.coinbase.com';
const COINBASE_WS_URL = 'wss://ws-feed.exchange.coinbase.com';
const COINBASE_MAX_CANDLES = 300;
const COINBASE_FOUR_HOUR_MS = 4 * 60 * 60 * 1000;
const COINBASE_RENDER_INTERVAL_MS = 100;

const COINBASE_GRANULARITY: Partial<Record<IChartTimeframe, number>> = {
  '1m': 60,
  '5m': 300,
  '15m': 900,
  '1h': 3600,
  '6h': 21600,
  '24h': 86400,
  '1d': 86400,
};

export type CoinbaseCandle = [
  time: number,
  low: string | number,
  high: string | number,
  open: string | number,
  close: string | number,
  volume: string | number,
];

type CoinbaseBookLevel = [price: string, size: string, ...metadata: unknown[]];

export type CoinbaseOrderbookMessage =
  | {
      type: 'snapshot';
      bids: CoinbaseBookLevel[];
      asks: CoinbaseBookLevel[];
    }
  | {
      type: 'l2update';
      changes: [side: 'buy' | 'sell', price: string, size: string][];
    };

export interface CoinbaseOrderbookState {
  bids: Map<string, string>;
  asks: Map<string, string>;
}

export const buildCoinbaseProductId = (base: string, quote: string) =>
  `${base.toUpperCase()}-${quote.toUpperCase()}`;

export const normalizeCoinbaseCandles = (candles: CoinbaseCandle[]): KLine[] =>
  candles
    .map(
      ([time, low, high, open, close, volume]) =>
        [
          Number(time) * 1000,
          String(open),
          String(high),
          String(low),
          String(close),
          String(volume),
        ] as KLine,
    )
    .sort((left, right) => left[0] - right[0]);

export const aggregateCoinbaseFourHourCandles = (candles: KLine[]): KLine[] => {
  const candlesByBucket = new Map<number, KLine[]>();

  [...candles]
    .sort((left, right) => left[0] - right[0])
    .forEach(candle => {
      const bucket = Math.floor(candle[0] / COINBASE_FOUR_HOUR_MS) * COINBASE_FOUR_HOUR_MS;
      const groupedCandles = candlesByBucket.get(bucket) ?? [];
      groupedCandles.push(candle);
      candlesByBucket.set(bucket, groupedCandles);
    });

  return [...candlesByBucket.entries()]
    .sort(([left], [right]) => left - right)
    .map(([timestamp, groupedCandles]) => {
      const first = groupedCandles[0];
      const last = groupedCandles[groupedCandles.length - 1];
      const highs = groupedCandles.map(candle => Number(candle[2]));
      const lows = groupedCandles.map(candle => Number(candle[3]));
      const volume = groupedCandles.reduce((total, candle) => total + Number(candle[5]), 0);

      return [
        timestamp,
        first[1],
        String(Math.max(...highs)),
        String(Math.min(...lows)),
        last[4],
        String(volume),
      ] as KLine;
    });
};

export const coinbasePrecisionFromIncrement = (increment: string | undefined): number => {
  if (!increment || !Number.isFinite(Number(increment)) || Number(increment) <= 0) {
    return 1;
  }

  const [, decimals = ''] = increment.split('.');
  return decimals.replace(/0+$/, '').length;
};

export const createCoinbaseOrderbookState = (): CoinbaseOrderbookState => ({
  bids: new Map(),
  asks: new Map(),
});

export const applyCoinbaseOrderbookMessage = (
  state: CoinbaseOrderbookState,
  message: CoinbaseOrderbookMessage,
) => {
  if (message.type === 'snapshot') {
    state.bids.clear();
    state.asks.clear();
    message.bids.forEach(([price, size]) => state.bids.set(price, size));
    message.asks.forEach(([price, size]) => state.asks.set(price, size));
    return;
  }

  message.changes.forEach(([side, price, size]) => {
    const levels = side === 'buy' ? state.bids : state.asks;
    if (Number(size) === 0) {
      levels.delete(price);
    } else {
      levels.set(price, size);
    }
  });
};

export const buildCoinbaseSubscription = (productId: string) => ({
  type: 'subscribe' as const,
  product_ids: [productId],
  // `level2` requires credentials. `level2_batch` is the public snapshot/update feed.
  channels: ['level2_batch', 'ticker', 'matches'],
});

interface CoinbaseProduct {
  quote_increment: string;
}

interface CoinbaseOrderbook {
  bids: CoinbaseBookLevel[];
  asks: CoinbaseBookLevel[];
}

interface CoinbaseStats {
  open: string;
  high: string;
  low: string;
  last: string;
  volume: string;
}

interface CoinbaseTrade {
  price: string;
  size: string;
  side: string;
  time: string;
}

type CoinbaseWsMessage =
  | CoinbaseOrderbookMessage
  | {
      type: 'ticker';
      price: string;
      open_24h: string;
      high_24h: string;
      low_24h: string;
      volume_24h: string;
    }
  | {
      type: 'match' | 'last_match';
      price: string;
      size: string;
      side: string;
      time: string;
    }
  | {
      type: 'error';
      message?: string;
      reason?: string;
    };

export class CoinbaseStrategy implements BaseStrategy {
  ws: ReconnectingWebSocket | undefined;
  pair: string;
  displayDepth: number;
  fetchDepth = 1000;
  precision = 1;
  selectedPrecision = 1;
  orderbook = createCoinbaseOrderbookState();
  orderbookPublishTimer: ReturnType<typeof setTimeout> | undefined;
  liveIntervalMs = 60000;
  currentKline: KLine | undefined;

  onOrderbookUpdate: (value: IOrderbook) => void;
  onTickerUpdate: (value: ITickerSocket) => void;
  onTradeUpdate: (value: TradeSocket) => void;
  klineUpdate: (value: KLine) => void;
  precisionUpdate: (value: number) => void;

  constructor({
    base,
    quote,
    orderbookDepth,
    onOrderbookUpdate,
    onTickerUpdate,
    onTradeUpdate,
    klineUpdate,
    precisionUpdate,
  }: IInitArgs) {
    this.pair = buildCoinbaseProductId(base, quote);
    this.displayDepth = orderbookDepth ?? 20;
    this.onOrderbookUpdate = onOrderbookUpdate;
    this.onTickerUpdate = onTickerUpdate;
    this.onTradeUpdate = onTradeUpdate;
    this.klineUpdate = klineUpdate;
    this.precisionUpdate = precisionUpdate;
  }

  publishOrderbook = () => {
    const asks = [...this.orderbook.asks.entries()]
      .sort((left, right) => Number(left[0]) - Number(right[0]))
      .slice(0, this.fetchDepth);
    const bids = [...this.orderbook.bids.entries()]
      .sort((left, right) => Number(right[0]) - Number(left[0]))
      .slice(0, this.fetchDepth);

    this.onOrderbookUpdate(
      aggregateOrderbook(asks, bids, this.selectedPrecision, this.displayDepth),
    );
  };

  scheduleOrderbookPublish = () => {
    if (this.orderbookPublishTimer) return;

    this.orderbookPublishTimer = setTimeout(() => {
      this.orderbookPublishTimer = undefined;
      this.publishOrderbook();
    }, COINBASE_RENDER_INTERVAL_MS);
  };

  fetchProduct = async () => {
    try {
      const response = await axios.get<CoinbaseProduct>(
        `${COINBASE_REST_BASE}/products/${this.pair}`,
      );
      const precision = coinbasePrecisionFromIncrement(response.data.quote_increment);
      this.precision = precision;
      this.selectedPrecision = precision;
      this.precisionUpdate(precision);
    } catch (error) {
      console.log('Coinbase product error:', error);
    }
  };

  fetchOrderbook = async () => {
    try {
      const response = await axios.get<CoinbaseOrderbook>(
        `${COINBASE_REST_BASE}/products/${this.pair}/book`,
        { params: { level: 2 } },
      );

      applyCoinbaseOrderbookMessage(this.orderbook, {
        type: 'snapshot',
        bids: response.data.bids,
        asks: response.data.asks,
      });
      this.publishOrderbook();
    } catch (error) {
      console.log('Coinbase orderbook error:', error);
    }
  };

  fetchTicker = async () => {
    try {
      const response = await axios.get<CoinbaseStats>(
        `${COINBASE_REST_BASE}/products/${this.pair}/stats`,
      );
      const { open, high, low, last, volume } = response.data;
      const openNumber = Number(open);
      const lastNumber = Number(last);
      const change =
        Number.isFinite(openNumber) && openNumber !== 0 && Number.isFinite(lastNumber)
          ? ((lastNumber - openNumber) / openNumber) * 100
          : 0;

      this.onTickerUpdate({
        baseVolume: volume,
        change: normalizeNumber(change),
        close: last,
        high,
        last: Number.isFinite(lastNumber) ? lastNumber.toFixed(this.precision) : last,
        low,
        quoteVolume: normalizeNumber(Number(volume) * lastNumber),
      });
    } catch (error) {
      console.log('Coinbase ticker error:', error);
    }
  };

  fetchKlines = async ({
    from,
    to,
    limit,
    interval,
  }: {
    base: string;
    quote: string;
    from: number;
    to: number;
    limit: number;
    interval: IChartTimeframe;
  }): Promise<KLine[]> => {
    const isFourHour = interval === '4h';
    const granularity = isFourHour ? 3600 : COINBASE_GRANULARITY[interval];
    if (!granularity) return [];

    const boundedLimit = Math.min(COINBASE_MAX_CANDLES, Math.max(1, limit));
    const sourceLimit = isFourHour
      ? Math.min(COINBASE_MAX_CANDLES, boundedLimit * 4)
      : boundedLimit;
    const endSeconds = to > 0 ? Math.floor(to / 1000) : Math.floor(Date.now() / 1000);
    const earliestSeconds = endSeconds - granularity * sourceLimit;
    const startSeconds =
      from > 0 ? Math.max(Math.floor(from / 1000), earliestSeconds) : earliestSeconds;

    try {
      const response = await axios.get<CoinbaseCandle[]>(
        `${COINBASE_REST_BASE}/products/${this.pair}/candles`,
        {
          params: {
            granularity,
            start: new Date(startSeconds * 1000).toISOString(),
            end: new Date(endSeconds * 1000).toISOString(),
          },
        },
      );

      const candles = normalizeCoinbaseCandles(response.data ?? []).slice(-sourceLimit);
      return isFourHour
        ? aggregateCoinbaseFourHourCandles(candles).slice(-boundedLimit)
        : candles.slice(-boundedLimit);
    } catch (error) {
      console.log('Coinbase klines error:', error);
      return [];
    }
  };

  fetchTrades = async ({
    limit,
  }: {
    base: string;
    quote: string;
    limit: number;
  }): Promise<TradeSocket[]> => {
    try {
      const response = await axios.get<CoinbaseTrade[]>(
        `${COINBASE_REST_BASE}/products/${this.pair}/trades`,
        { params: { limit } },
      );

      return (response.data ?? []).map(trade => ({
        price: trade.price,
        quantity: trade.size,
        timestamp: String(Date.parse(trade.time)),
        side: trade.side,
      }));
    } catch (error) {
      console.log('Coinbase trades error:', error);
      return [];
    }
  };

  updateKlineFromTrade = (price: string, size: string, time: string) => {
    const timestampMs = Date.parse(time);
    const priceNumber = Number(price);
    const sizeNumber = Number(size);
    if (
      !Number.isFinite(timestampMs) ||
      !Number.isFinite(priceNumber) ||
      !Number.isFinite(sizeNumber)
    ) {
      return;
    }

    const bucketSeconds =
      Math.floor(timestampMs / this.liveIntervalMs) * (this.liveIntervalMs / 1000);

    if (!this.currentKline || this.currentKline[0] !== bucketSeconds) {
      this.currentKline = [bucketSeconds, price, price, price, price, size];
    } else {
      this.currentKline = [
        bucketSeconds,
        this.currentKline[1],
        String(Math.max(Number(this.currentKline[2]), priceNumber)),
        String(Math.min(Number(this.currentKline[3]), priceNumber)),
        price,
        normalizeNumber(Number(this.currentKline[5]) + sizeNumber),
      ];
    }

    this.klineUpdate(this.currentKline);
  };

  connectWebSocket = ({ interval }: { interval: number }) => {
    this.closeWebSocket();
    this.liveIntervalMs = interval > 0 ? interval : 60000;

    const ws = new ReconnectingWebSocket(COINBASE_WS_URL, [], {
      maxRetries: 10,
      minReconnectionDelay: 1000,
    });
    this.ws = ws;

    ws.onopen = () => {
      this.orderbook.bids.clear();
      this.orderbook.asks.clear();
      this.currentKline = undefined;
      ws.send(JSON.stringify(buildCoinbaseSubscription(this.pair)));
    };

    ws.onmessage = event => {
      try {
        if (typeof event.data !== 'string') return;
        const message = JSON.parse(event.data) as CoinbaseWsMessage;

        if (message.type === 'snapshot' || message.type === 'l2update') {
          applyCoinbaseOrderbookMessage(this.orderbook, message);
          if (message.type === 'snapshot') {
            this.publishOrderbook();
          } else {
            this.scheduleOrderbookPublish();
          }
          return;
        }

        if (message.type === 'ticker') {
          const lastNumber = Number(message.price);
          const openNumber = Number(message.open_24h);
          const change =
            Number.isFinite(openNumber) && openNumber !== 0 && Number.isFinite(lastNumber)
              ? ((lastNumber - openNumber) / openNumber) * 100
              : 0;

          this.onTickerUpdate({
            baseVolume: message.volume_24h,
            change: normalizeNumber(change),
            close: message.price,
            high: message.high_24h,
            last: Number.isFinite(lastNumber) ? lastNumber.toFixed(this.precision) : message.price,
            low: message.low_24h,
            quoteVolume: normalizeNumber(Number(message.volume_24h) * lastNumber),
          });
          return;
        }

        if (message.type === 'match' || message.type === 'last_match') {
          this.onTradeUpdate({
            price: message.price,
            quantity: message.size,
            timestamp: String(Date.parse(message.time)),
            side: message.side,
          });
          this.updateKlineFromTrade(message.price, message.size, message.time);
          return;
        }

        if (message.type === 'error') {
          console.log('Coinbase websocket subscription error:', message.message, message.reason);
        }
      } catch (error) {
        console.log('Coinbase websocket error:', error);
      }
    };
  };

  closeWebSocket = () => {
    this.ws?.close();
    this.ws = undefined;
    if (this.orderbookPublishTimer) {
      clearTimeout(this.orderbookPublishTimer);
      this.orderbookPublishTimer = undefined;
    }
  };

  switchOrderbookTick = ({ tick }: { tick: number }) => {
    this.selectedPrecision = tick;
    this.publishOrderbook();
  };

  setOrderbookDepth = (depth: number) => {
    this.displayDepth = depth;
    this.publishOrderbook();
  };

  getOrderbookTicks = (precision: number) =>
    Array.from({ length: precision })
      .fill(0)
      .map((_, index) => index + 1)
      .map(value => ({
        value: normalizeNumber(1 / 10 ** value),
        tick: value,
        id: value,
      }))
      .slice(-3);

  fetch = async () => {
    await Promise.all([this.fetchProduct(), this.fetchOrderbook(), this.fetchTicker()]);
  };

  static init = async (args: IInitArgs) => {
    const strategy = new CoinbaseStrategy(args);
    await strategy.fetch();
    return strategy;
  };
}
